import type { FormNode, FormSchema } from '../../../../types/form.types'

/**
 * Flow layout (spec 2026-10-28): a container's children are laid out in array order, left to right,
 * on a fixed 12-column grid; width is a preset colSpan; `lineBreak` forces a new row. row/col are
 * derived here and nowhere else, so every edit path stays consistent.
 */

export const GRID_COLUMNS = 12
export const WIDTH_PRESETS: readonly number[] = [3, 4, 6, 8, 12]
export const WIDTH_LABELS: Record<number, string> = { 3: '¼', 4: '⅓', 6: '½', 8: '⅔', 12: 'Full' }
/** Empty columns before an element in its row (keeps a right-aligned field right-aligned). */
export const OFFSET_PRESETS: readonly number[] = [0, 3, 4, 6, 8]
export const OFFSET_LABELS: Record<number, string> = { 0: 'None', 3: '¼', 4: '⅓', 6: '½', 8: '⅔' }

const FULL_WIDTH_TYPES = new Set(['Table', 'Pages', 'CommandBar'])

export function isFullWidth(node: Pick<FormNode, 'type'>): boolean {
  return FULL_WIDTH_TYPES.has(node.type)
}

function snapTo(presets: readonly number[], value: number): number {
  let best = presets[presets.length - 1]
  let bestDistance = Infinity
  for (const preset of presets) {
    const distance = Math.abs(preset - value)
    if (distance <= bestDistance) {
      best = preset
      bestDistance = distance
    }
  }
  return best
}

export function snapOffset(offset: number): number {
  return snapTo(OFFSET_PRESETS, offset)
}

export function snapWidth(span: number): number {
  return snapTo(WIDTH_PRESETS, span)
}

export function stepWidth(span: number, step: 1 | -1): number {
  const index = WIDTH_PRESETS.indexOf(snapWidth(span))
  const next = Math.max(0, Math.min(WIDTH_PRESETS.length - 1, index + step))
  return WIDTH_PRESETS[next]
}

export function reflowNodes(nodes: FormNode[]): FormNode[] {
  let row = -1
  let x = GRID_COLUMNS
  let previousFull = false
  return nodes.map((node) => {
    const full = isFullWidth(node)
    const span = full ? GRID_COLUMNS : snapWidth(node.colSpan)
    const wanted = full ? 0 : snapOffset(node.offset ?? 0)
    if (full || previousFull || node.lineBreak || x + wanted + span > GRID_COLUMNS) {
      row += 1
      x = 0
    }
    const offset = Math.min(wanted, GRID_COLUMNS - span)
    const { offset: _old, ...rest } = node
    const placed: FormNode = { ...rest, row, col: x + offset, colSpan: span, ...(offset ? { offset } : {}) }
    if (node.children) placed.children = reflowNodes(node.children)
    x += offset + span
    previousFull = full
    return placed
  })
}

function commandBarsFirst(nodes: FormNode[]): FormNode[] {
  return [...nodes.filter((n) => n.type === 'CommandBar'), ...nodes.filter((n) => n.type !== 'CommandBar')]
}

export function reflowSchema(schema: FormSchema): FormSchema {
  return { ...schema, gridColumns: GRID_COLUMNS, root: reflowNodes(commandBarsFirst(schema.root)) }
}

function fromLegacyNodes(nodes: FormNode[]): FormNode[] {
  const sorted = [...nodes].sort((a, b) => a.row - b.row || a.col - b.col)
  const rowTotal = new Map<number, number>()
  for (const node of sorted) rowTotal.set(node.row, Math.max(rowTotal.get(node.row) ?? 0, node.col + node.colSpan))
  let x = 0
  let previousRow: number | null = null
  let legacyEnd = 0
  return sorted.map((node) => {
    const full = isFullWidth(node)
    const total = Math.max(1, rowTotal.get(node.row) ?? 1)
    const span = full ? GRID_COLUMNS : snapWidth((GRID_COLUMNS * node.colSpan) / total)
    let lineBreak = node.lineBreak === true
    if (previousRow !== node.row) legacyEnd = 0
    // An empty cell left of the field in its legacy row becomes an offset.
    const offset = full ? 0 : Math.min(snapOffset((GRID_COLUMNS * Math.max(0, node.col - legacyEnd)) / total), GRID_COLUMNS - span)
    legacyEnd = Math.max(legacyEnd, node.col + node.colSpan)
    if (previousRow !== null && node.row !== previousRow) {
      if (x + offset + span <= GRID_COLUMNS) lineBreak = true
      x = 0
    }
    // Mirror reflowNodes: a span that does not fit wraps, so x tracks where packing actually places the node.
    if (x + offset + span > GRID_COLUMNS) x = 0
    x += offset + span
    previousRow = node.row
    const { lineBreak: _old, offset: _oldOffset, ...rest } = node
    const converted: FormNode = {
      ...rest, colSpan: span, ...(lineBreak ? { lineBreak: true } : {}), ...(offset ? { offset } : {}),
    }
    if (node.children) converted.children = fromLegacyNodes(node.children)
    return converted
  })
}

/** Legacy per-row sizing -> flow layout. A form already in the model is re-reflowed (edited elsewhere: agent, array-only moves). */
export function fromLegacySchema(schema: FormSchema): FormSchema {
  if (schema.gridColumns === GRID_COLUMNS) return reflowSchema(schema)
  return reflowSchema({ ...schema, root: fromLegacyNodes(schema.root) })
}
